// CTV-610: pure geometry/clamping for the background-video timeline editor.
// Clips are laid contiguously (no gaps) across a fixed TIMELINE_SEC-wide track;
// each clip plays a window [inSec, inSec+lengthSec] of its source. Kept pure so
// the drag interactions in ClipTimeline.tsx stay thin and this stays testable.

import { TARGET_DURATION_SEC } from './backgroundVideoApi';

// The track is exactly the locked render length. Clips can fill up to this, no
// further; any shortfall is freeze-padded at render time.
export const TIMELINE_SEC = TARGET_DURATION_SEC;
export const MIN_CLIP_SEC = 0.5;

export interface TimelineClip {
  id: string;
  storagePath: string;
  name: string;
  /** Local object URL for in-editor preview (not sent to the server). */
  previewUrl: string;
  /** Source length in seconds (0 until the clip's metadata has loaded). */
  durationSec: number;
  /** In-point into the source. */
  inSec: number;
  /** How long the clip plays on the master timeline. */
  lengthSec: number;
  /** Filmstrip frames across the whole source (empty until extracted). */
  thumbnails?: { t: number; url: string }[];
}

const round = (n: number) => Math.round(n * 100) / 100;

// Where the landing-page text choreography switches "section" on the 24s loop:
// the headline block (0-10, which includes the 0-3 clean intro since it's the
// same footage before the blur fades in), the streaming pitch (10-16), and the
// "Your Ad Here" slate (16-24). A clip cut aligned to one of these transitions
// reads as intentional, so the editor marks them and snaps cuts to them.
// Derived from the scene timing in SelfServiceLanding.tsx — keep in sync.
export const TIMELINE_SECTIONS: { start: number; label: string }[] = [
  { start: 0, label: 'Headline' },
  { start: 10, label: 'Pitch' },
  { start: 16, label: 'Ad slate' },
];
// The section boundaries (skip 0), i.e. the clean cut points, as snap targets.
export const SECTION_CUTS = TIMELINE_SECTIONS.slice(1).map((s) => s.start);
export const SNAP_SEC = 0.4;

/**
 * If a clip's cut (its end = startSec + length) lands within SNAP_SEC of a
 * section boundary, snap the length so the cut sits exactly on it.
 */
export function snapCutLength(startSec: number, length: number, cuts = SECTION_CUTS): number {
  const end = startSec + length;
  const near = cuts.find((c) => Math.abs(c - end) <= SNAP_SEC);
  return near === undefined ? length : round(near - startSec);
}

/** Sum of every clip's play length. */
export function totalLength(clips: TimelineClip[]): number {
  return round(clips.reduce((s, c) => s + c.lengthSec, 0));
}

/** Start time of clip `index` (contiguous: sum of prior lengths). */
export function startAt(clips: TimelineClip[], index: number): number {
  return round(clips.slice(0, index).reduce((s, c) => s + c.lengthSec, 0));
}

/**
 * Clamp a requested play length for clip `index`:
 *  - at least MIN_CLIP_SEC,
 *  - no more source than exists after its in-point,
 *  - not so long the whole track exceeds TIMELINE_SEC.
 */
export function clampLength(clips: TimelineClip[], index: number, requested: number): number {
  const clip = clips[index];
  const othersTotal = totalLength(clips) - clip.lengthSec;
  const maxByTrack = TIMELINE_SEC - othersTotal;
  const maxBySource = (clip.durationSec || TIMELINE_SEC) - clip.inSec;
  return round(Math.max(MIN_CLIP_SEC, Math.min(requested, maxByTrack, maxBySource)));
}

/**
 * Clamp a requested in-point for clip `index` while holding its length fixed:
 * the window [in, in+length] must stay inside the source.
 */
export function clampIn(clips: TimelineClip[], index: number, requested: number): number {
  const clip = clips[index];
  const maxIn = Math.max(0, (clip.durationSec || 0) - clip.lengthSec);
  return round(Math.min(Math.max(0, requested), maxIn));
}

/** A sensible starting length for a freshly added clip: fill remaining space. */
export function defaultLength(durationSec: number, currentTotal: number): number {
  const remaining = Math.max(0, TIMELINE_SEC - currentTotal);
  const cap = durationSec > 0 ? Math.min(durationSec, remaining) : remaining;
  return round(Math.max(MIN_CLIP_SEC, cap || MIN_CLIP_SEC));
}

/**
 * Which clip — and what source-time within it — is playing at cumulative
 * timeline second `cum`. Used to sync the preview <video> to the playhead.
 * Beyond the filled length it clamps to the last clip's final frame.
 */
export function clipAtCum(
  clips: TimelineClip[],
  cum: number,
): { index: number; localSec: number } {
  const c = Math.max(0, cum);
  let acc = 0;
  for (let i = 0; i < clips.length; i++) {
    const len = clips[i].lengthSec;
    if (c < acc + len || i === clips.length - 1) {
      const within = Math.max(0, Math.min(len, c - acc));
      return { index: i, localSec: round(clips[i].inSec + within) };
    }
    acc += len;
  }
  return { index: 0, localSec: 0 };
}

/** Map to the render payload: each clip is its trimmed window. */
export function toRenderClips(
  clips: TimelineClip[],
): { storagePath: string; inSec?: number; outSec: number }[] {
  return clips.map((c) => ({
    storagePath: c.storagePath,
    ...(c.inSec > 0 ? { inSec: round(c.inSec) } : {}),
    outSec: round(c.inSec + c.lengthSec),
  }));
}
